import type { ReactElement } from 'react';
import type { ActivationPlugin } from '@hub/plugin-sdk';
import type { LeadData, PluginConfig } from '@hub/shared-types';
import { PluginErrorBoundary } from '@/presentation/components/PluginErrorBoundary';

export interface PluginActiveScreenProps {
  plugin: ActivationPlugin;
  config: PluginConfig;
  onLeadCapture: (lead: LeadData) => void;
  onComplete: () => void;
  onRenderError: (error: unknown) => void;
}

/**
 * Monta o componente do plugin real dentro de um `PluginErrorBoundary`
 * dedicado (HUB-237, CA-05) — nunca a página inteira. `mode: 'live'`
 * (contrato `PluginProps`, ADR-004): execução real no evento, captura de
 * leads persistida (via `onLeadCapture` -> `CapturePluginLeadUseCase`).
 *
 * Quem controla o `key` de remontagem (`mountKey`, forçando recarregar o
 * módulo do zero a cada nova tentativa) é o chamador (`ActivationFlow`), não
 * este componente — ver `usePluginLoadRetry`.
 */
export function PluginActiveScreen({
  plugin: activationPlugin,
  config,
  onLeadCapture,
  onComplete,
  onRenderError,
}: PluginActiveScreenProps): ReactElement {
  const PluginComponent = activationPlugin.Component;
  return (
    <PluginErrorBoundary onError={onRenderError}>
      <PluginComponent
        config={config}
        onLeadCapture={onLeadCapture}
        onComplete={onComplete}
        mode="live"
      />
    </PluginErrorBoundary>
  );
}
